import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { Store } from '../types';
import { ApiError, api } from './api';

interface StoreState {
  store: Store | null;
  loading: boolean;
  error: ApiError | Error | null;
  refetch: () => void;
}

const StoreContext = createContext<StoreState | null>(null);

/**
 * Single shared store bootstrap (/api/admin/me). Every consumer of useStore
 * reads the SAME object, so a refetch after e.g. a plan change updates the
 * sidebar badge and all pages at once — no per-component stale copies.
 */
export function StoreProvider({ children }: { children: ReactNode }): ReactNode {
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { store: s } = await api.me();
      setStore(s);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load store'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <StoreContext.Provider value={{ store, loading, error, refetch: () => void load() }}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore(): StoreState {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>');
  return ctx;
}
